import {
  Dropdown,
  Flex,
  Icon,
  Nav,
  NavBrand,
  NavEnd,
  NavItem,
  Span,
  LogoSmall,
  Button,
  NewTextLogo,
} from "@goodfill/ui-components";
import { useCallback, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { ABOUT_US_URL, BLOGS_URL, HELP_URL, route, WEBINAR_URL } from "../../routes";
import { SearchIcon } from "./SearchIcon";
import { userAuthState, userCustomerApplicationState, userCustomerApplicationWizardState } from "../../state";
import "./TopBar.scss";
import { useLogout } from "../useLogout";
import { LanguageSwitcher } from "./LanguageSwitcher";

const GOODFILL_URL = import.meta.env.VITE_GOODFILL_URL;

export const TopBar = () => {
  const navigate = useNavigate();

  const [userAuthData] = useRecoilState(userAuthState);
  const [userCustomerApplicationData] = useRecoilState(userCustomerApplicationState);
  const [wizardStateData] = useRecoilState(userCustomerApplicationWizardState);

  const isUserLoggedIn = userAuthData !== null;
  const fullName = `${
    userCustomerApplicationData?.submission.personalInformation.name.first ??
    wizardStateData?.personalInformation?.name?.first ??
    ""
  } ${
    userCustomerApplicationData?.submission.personalInformation.name.last ??
    wizardStateData?.personalInformation?.name?.last ??
    ""
  }`;

  const onClickBrand = useCallback(() => {
    navigate(route.dashboard());
  }, [navigate]);

  const onClickAccount = useCallback(() => {
    navigate(route.personalInformation());
  }, [navigate]);
  const onClickPortfolio = useCallback(() => {
    navigate(route.accountPortfolio());
  }, [navigate]);

  const onClickApplication = useCallback(() => {
    navigate(route.customerApplication(1));
  }, [navigate]);

  const logout = useLogout();

  const onClickLogout = () => {
    logout();
  };

  const [dropdownIsClose, setDropdownIsClose] = useState(true);

  const [screenSize, setScreenSize] = useState(window.outerWidth);

  const [profileDropdownIsOpen, setProfileDropdownIsOpen] = useState(false);

  const navItemClassName =
    "whitespace-nowrap select-none text-white hover:font-bold hover:text-white hover:no-underline";

  useEffect(() => {
    window.addEventListener("resize", handleToggleHamburger);

    if (screenSize >= 768) {
      setDropdownIsClose(true);
    }

    // cleanup this component
    return () => {
      window.removeEventListener("resize", handleToggleHamburger);
    };
  }, [screenSize]);

  const handleToggleHamburger = () => {
    setScreenSize(window.outerWidth);
  };

  const onClickDropdownTrigger = () => {
    setDropdownIsClose((bool) => !bool);
  };

  const setIsOpenDropdownFilter = (isOpen: boolean) => {
    // only close if we are not docked
    setProfileDropdownIsOpen(isOpen);
  };

  return (
    <Nav primaryGradient={true}>
      <NavBrand onClick={onClickBrand}>
        <Flex viewport="medium" className="show-md">
          <NewTextLogo />
        </Flex>
        <Flex viewport="small">
          <LogoSmall />
        </Flex>
      </NavBrand>
      <Flex viewport="small">
        <Icon name="hamburger" onClick={onClickDropdownTrigger} size={32} />
      </Flex>

      <Flex
        viewport="medium"
        className={`${dropdownIsClose === true ? "close-dropdown" : "open-dropdown"} main-menu show-md`}
      >
        <Flex viewport="small" items="end" className="cross-btn-container">
          <Icon name="cross" onClick={onClickDropdownTrigger} size={32} />
        </Flex>

        <NavEnd>
          {!isUserLoggedIn && (
            <a title="GoodFill" href={GOODFILL_URL} className={navItemClassName}>
              Goodfill
            </a>
          )}

          <a title="About Us" href={ABOUT_US_URL} className={navItemClassName}>
            About Us
          </a>

          {isUserLoggedIn && (
            <>
              <Link title="Bonds" to={route.dashboard()} className={navItemClassName}>
                Bonds
              </Link>
            </>
          )}

          <a title="Blog" href={BLOGS_URL} className={navItemClassName}>
            Blog
          </a>

          <a title="Help" href={HELP_URL} className={navItemClassName}>
            Help
          </a>

          <LanguageSwitcher />

          {!isUserLoggedIn && (
            <Flex gap={4} className="flex-wrap">
              <Flex className="sm-w-full">
                <Button
                  fill
                  outlined={true}
                  uppercase={false}
                  blue={true}
                  size="medium"
                  onClick={() => navigate(route.signIn())}
                >
                  Log In
                </Button>
              </Flex>
              <Flex className="sm-w-full">
                <Button fill className="blue-primary" size="medium" onClick={() => navigate(route.signUp())}>
                  Sign Up
                </Button>
              </Flex>
            </Flex>
          )}

          <NavItem>
            <SearchIcon />
          </NavItem>

          {isUserLoggedIn ? (
            <NavItem>
              <Flex>
                <img
                  className="w-10 h-10 rounded-full"
                  src="https://e7.pngegg.com/pngimages/799/987/png-clipart-computer-icons-avatar-icon-design-avatar-heroes-computer-wallpaper-thumbnail.png"
                  alt="Rounded avatar"
                />

                <Dropdown
                  style={{ display: "flex" }}
                  dropdownMenuClass="profile-dropdown"
                  isOpen={profileDropdownIsOpen}
                  setIsOpen={setIsOpenDropdownFilter}
                  fixed={false}
                  right={false}
                  trigger={
                    <div className="flex items-center">
                      <Span white={true} className="font-normal">
                        Hi, {fullName}
                      </Span>
                      <Icon
                        style={{ color: "white" }}
                        className="profile-dropdown-icon"
                        name={profileDropdownIsOpen ? "caret-up" : "caret-down"}
                        size={12}
                      />
                    </div>
                  }
                >
                  <div className="profile-dropdown-ul">
                    <NavItem customClass="profile-dropdown-items" onClick={onClickPortfolio}>
                      <div>Portfolio</div>
                    </NavItem>
                    <NavItem customClass="profile-dropdown-items" onClick={onClickAccount}>
                      <div>Account</div>
                    </NavItem>
                    <NavItem customClass="profile-dropdown-items" onClick={onClickApplication}>
                      <div>Application</div>
                    </NavItem>
                    <NavItem customClass="profile-dropdown-items">
                      <div onClick={onClickLogout}>Log out</div>
                    </NavItem>
                  </div>
                </Dropdown>
              </Flex>
            </NavItem>
          ) : null}
        </NavEnd>
      </Flex>
    </Nav>
  );
};
